import { useEffect, useState } from 'preact/hooks'
import { useSignal } from '@preact/signals'
import { useLocation } from 'wouter-preact'

import { Stepper } from 'pages/settings/teams/components/Stepper'
import {
	Button,
	HorizontalDivider,
	Input,
	TextLink,
	Typography
} from 'components'
import { plansState, getPlans, application } from 'state'

import { ComparisonTable, PricingCard, CardInfo } from './components'
import { usePayments } from '../../contexts/PaymentsContext'
import { setTeamName, teamName } from '../../state'

const SetupTeamPage = () => {
	const plans = useSignal(plansState)
	const { selectedPlan, setSelectedPlan, setClientSecret, setReturnUrl } =
		usePayments()
	const [isPlansFetching, setIsPlansFetching] = useState(true)
	const [, navigate] = useLocation()
	const isLoading = useSignal(false)

	useEffect(() => {
		const fetchPlans = async () => {
			await getPlans()
			setIsPlansFetching(false)
		}
		fetchPlans()
	}, [])

	const handleSelectPlan = (planId: string) => {
		setSelectedPlan(planId)
	}

	const handleContinue = async () => {
		if (selectedPlan) {
			isLoading.value = true

			const data = await application
				.peek()
				.forManagingEntitlements()
				.acquirePlan(selectedPlan)

			if (data.data?.clientSecret) {
				setClientSecret(data.data.clientSecret)
				setReturnUrl(data.data.returnUrl)
				navigate(
					`/payment-details?payment_intent_client_secret=${data.data.clientSecret}&return_url=${data.data.returnUrl}`
				)
			} else {
				// this is a free plan that does not returns a client_secret
				alert('Error missing client_secret')
			}
		}

		isLoading.value = false
	}

	const filteredPlans = plans.value?.peek().filter(p => p.type !== 'free')

	return (
		<div class='flex-1'>
			<div class='flex grid-cols-1 flex-col-reverse gap-10 xl:grid xl:grid-cols-[2fr_1fr]'>
				<div>
					<Typography
						color='text-black-100'
						text='Setup your Team'
						variant='h3'
					/>
					<Typography
						class='mt-2'
						color='text-black-200'
						text='Assign a name and subscribe to a paid plan.'
						variant='text1'
					/>
				</div>
				<Stepper
					steps={[
						{ number: 1, completed: false, current: true },
						{ number: 2, completed: false, current: false },
						{ number: 3, completed: false, current: false }
					]}
				/>
			</div>

			<HorizontalDivider className='my-16 xl:my-10' />

			<div class='flex grid-cols-1 flex-col-reverse gap-10 xl:grid xl:grid-cols-[2fr_1fr]'>
				<div>
					<Typography
						class='mb-4'
						text='Team name'
						variant='h4'
						weight='font-semibold'
					/>

					<div class='grid grid-cols-1 gap-8 lg:grid-cols-2'>
						<Input
							error={teamName.value.error}
							hasError={!!teamName.value.error}
							helperText='At least 3 characters, up to 36 allowed'
							id='team-name'
							onInput={setTeamName}
							placeholder='Enter your team name'
							type='text'
							value={teamName.value.value}
						/>
					</div>

					<HorizontalDivider className='my-16 xl:my-10' />

					<Typography
						class='mb-4'
						text='Select a Plan'
						variant='h4'
						weight='font-semibold'
					/>

					<div className='mb-5 grid grid-cols-1 gap-5 lg:grid-cols-2'>
						{filteredPlans.map(plan => (
							<PricingCard
								description={plan.description}
								handleSelectPlan={handleSelectPlan}
								isSelected={selectedPlan === plan.id}
								key={plan.id}
								planId={plan.id}
								planName={plan.name}
								price={plan.total / 100}
							/>

							// <div
							// 	className={`group flex flex-col rounded-lg border bg-black-800 p-6 transition-all hover:border-product ${
							// 		selectedPlan === plan.id
							// 			? 'border-2 border-product'
							// 			: 'border-black-700 border'
							// 	}`}
							// 	key={plan.id}
							// 	onClick={() => handleSelectPlan(plan.id)}
							// 	onKeyDown={e => {
							// 		if (e.key === 'Enter' || e.key === ' ') {
							// 			handleSelectPlan(plan.id)
							// 		}
							// 	}}
							// 	role='button'
							// 	tabIndex={0}
							// >
							// 	<div className='mb-8'>
							// 		<Typography
							// 			class='mb-2'
							// 			color={
							// 				selectedPlan === plan.id
							// 					? 'text-product'
							// 					: 'text-black-100'
							// 			}
							// 			text={plan.name}
							// 			variant='h4'
							// 		/>
							// 		<Typography
							// 			class='min-h-[60px]'
							// 			color='text-black-200'
							// 			text={plan.description}
							// 			variant='text2'
							// 		/>
							// 	</div>

							// 	<div className='mb-6 flex items-baseline'>
							// 		<Typography
							// 			color={
							// 				selectedPlan === plan.id
							// 					? 'text-product'
							// 					: 'text-black-100'
							// 			}
							// 			text={`$${plan.total / 100}`}
							// 			variant='h3'
							// 		/>
							// 		<Typography
							// 			class='ml-1'
							// 			color='text-black-200'
							// 			text='/ year'
							// 			variant='text2'
							// 		/>
							// 	</div>

							// 	<ul className='mb-8 space-y-4'>
							// 		{plan.listItems.map(item => (
							// 			<li className='flex items-start' key={item.id}>
							// 				<span className='mr-2 text-product'>•</span>
							// 				<Typography
							// 					color='text-black-200'
							// 					text={`${item.currencyValue} ${item.entitlement?.product.name || ''}`}
							// 					variant='text2'
							// 				/>
							// 			</li>
							// 		))}
							// 	</ul>
							// </div>
						))}
					</div>

					{!isPlansFetching && <ComparisonTable />}

					<HorizontalDivider className='my-16 xl:my-10' />

					<div class='mb-10 mt-6 flex flex-col-reverse gap-4 md:flex-row md:justify-between xl:mb-0'>
						<TextLink
							ariaLabel='Back'
							href='/'
							text='Back'
							variant='secondary'
						/>
						<Button
							className='mb-5 md:mb-0'
							disabled={isLoading.value || isPlansFetching}
							id='submit'
							isLoading={isLoading.value}
							onClick={handleContinue}
							text='Acquire Seats'
						/>
					</div>
				</div>

				<CardInfo />
			</div>
		</div>
	)
}

export { SetupTeamPage }
